<script setup lang="ts">
  import { ref, onMounted, computed, watch } from 'vue';
  import { storeToRefs } from 'pinia';
  import DocumentProgressBar from './DocumentProgressBar.vue';
  import { SelectInput } from '@/shared/ui';
  import type { FileStatus } from '../model/upload/types';
  import { useFileUploadStore, useDocumentUsers } from '@/features/Document';
  import { useFileValidation } from '../model';
  import { MAX_CHANGELOG_LENGTH } from '@/shared/config';
  const props = defineProps<{
    files: File[];
    getFileProgress: (file: File) => number;
    getFileStatus: (file: File) => FileStatus;
    documentIds: number[];
    getDocumentIdForFile?: (file: File) => number | undefined;
    multiple: boolean;
    isExternal?: boolean;
    isReplacement?: boolean;
  }>();

  const selectedManagers = ref<{ file: File; managerId: number }[]>([]);
  const { userOptions, isLoading, fetchUsers } = useDocumentUsers({ excludeCurrentUser: false });
  const selectedChangelogs = ref<{ file: File; changelog: string }[]>([]);
  const focusedChangelogs = ref<{ file: File; hasBeenFocused: boolean }[]>([]);
  const fileUploadStore = useFileUploadStore();
  const { updateFileManager, updateFileChangelog, getDocumentNameForFile, updateFileDocumentName } =
    fileUploadStore;
  const { uploadBatchDuplicateTitles, dbPrefixConflicts } = storeToRefs(fileUploadStore);
  const { extractPrefixFromFileName } = useFileValidation();

  const isTitleDuplicateInBatch = (file: File): boolean => {
    const name = getDocumentNameForFile(file).trim().toLowerCase();
    if (!name) return false;
    return uploadBatchDuplicateTitles.value.includes(name);
  };

  const isFilePrefixConflicting = (file: File): boolean => {
    const prefix = extractPrefixFromFileName(file.name);
    return prefix !== null && dbPrefixConflicts.value.includes(prefix);
  };

  const handleDocumentNameUpdate = (file: File, value: string) => {
    updateFileDocumentName(file, value);
  };

  const returnManager = (file: File): number | '' => {
    const manager = selectedManagers.value.find((gestor) => gestor.file.name === file.name);
    return manager?.managerId ?? '';
  };

  const returnChangelog = (file: File): string => {
    const changelog = selectedChangelogs.value.find(
      (changelog) => changelog.file.name === file.name
    );
    return changelog?.changelog ?? '';
  };

  const managerSelectOptions = computed(() => {
    if (isLoading.value) {
      return [{ id: '', name: 'Carregando gestores...', disabled: true }];
    }
    if (userOptions.value.length > 0) {
      return userOptions.value;
    }
    return [{ id: '', name: 'Nenhum gestor encontrado', disabled: true }];
  });

  const hasChangelogBeenFocused = (file: File): boolean => {
    const focused = focusedChangelogs.value.find((focused) => focused.file.name === file.name);
    return focused?.hasBeenFocused ?? false;
  };

  const handleChangelogBlur = (file: File): void => {
    const alreadyFocusedIndex = focusedChangelogs.value.findIndex(
      (focused) => focused.file.name === file.name
    );

    if (alreadyFocusedIndex !== -1) {
      focusedChangelogs.value[alreadyFocusedIndex] = {
        file,
        hasBeenFocused: true,
      };
    } else {
      focusedChangelogs.value.push({ file, hasBeenFocused: true });
    }
  };

  const shouldShowChangelogError = (file: File): boolean => {
    const changelog = returnChangelog(file);
    const hasBeenFocused = hasChangelogBeenFocused(file);
    return hasBeenFocused && !changelog.trim();
  };

  const shouldShowChangelogLengthError = (file: File): boolean => {
    const changelog = returnChangelog(file);
    return changelog.length > MAX_CHANGELOG_LENGTH;
  };

  const handleUpdateModelValue = (id: number | string, file: File): void => {
    const managerId = typeof id === 'string' ? Number(id) : id;

    // Store in the composable for the request
    updateFileManager(file, managerId);

    // Store locally for UI state
    const managerAlreadyExistsIndex = selectedManagers.value.findIndex(
      (gestor) => gestor.file.name === file.name
    );

    if (managerAlreadyExistsIndex !== -1) {
      selectedManagers.value[managerAlreadyExistsIndex] = {
        file,
        managerId,
      };
    } else {
      selectedManagers.value.push({ file, managerId });
    }

    checkAllChangelogsFilled();
  };

  const handleChangelogUpdate = (changelog: string, file: File): void => {
    // Store in the composable for the request
    updateFileChangelog(file, changelog);

    // Store locally for UI state
    const changelogAlreadyExistsIndex = selectedChangelogs.value.findIndex(
      (changelogEntry) => changelogEntry.file.name === file.name
    );

    if (changelogAlreadyExistsIndex !== -1) {
      selectedChangelogs.value[changelogAlreadyExistsIndex] = {
        file,
        changelog,
      };
    } else {
      selectedChangelogs.value.push({ file, changelog });
    }

    emit('changelogUpdated', file, changelog);

    checkAllChangelogsFilled();
  };

  const emit = defineEmits<{
    fileDeleted: [documentId: number];
    changelogUpdated: [file: File, changelog: string];
    allChangelogsFilled: [filled: boolean];
  }>();

  const hasFetchedUsers = ref(false);

  watch(
    () => props.files.length,
    (length) => {
      if (length > 0 && !hasFetchedUsers.value && !props.isExternal) {
        hasFetchedUsers.value = true;
        fetchUsers();
      }
    },
    { immediate: true }
  );

  onMounted(() => {
    checkAllChangelogsFilled();
  });

  const checkAllChangelogsFilled = () => {
    if (props.isExternal) {
      emit('allChangelogsFilled', true);
      return true;
    }

    const allFilled = props.files.every((file) => {
      const changelog = returnChangelog(file);
      const manager = returnManager(file);
      return (
        changelog.trim().length > 0 && changelog.length <= MAX_CHANGELOG_LENGTH && manager !== ''
      );
    });

    emit('allChangelogsFilled', allFilled);
    return allFilled;
  };
</script>

<template>
  <div>
    <div
      v-for="(file, index) in files"
      :key="`file-${index}-${file.name}-${file.size}-${file.lastModified}`"
      class="w-full border border-dashed rounded-xl p-6"
      :class="[
        files.length > 1 ? 'mb-6' : '',
        isFilePrefixConflicting(file) ? 'border-red-16' : 'border-gray-02',
      ]"
    >
      <DocumentProgressBar
        :get-file-progress="getFileProgress"
        :file="file"
        :get-file-status="getFileStatus"
        :document-id="getDocumentIdForFile ? getDocumentIdForFile(file) : undefined"
        class="px-6 py-4"
        @file-deleted="(id) => emit('fileDeleted', id)"
      />
      <div
        v-if="isExternal && !isReplacement"
        class="flex flex-col mt-4"
      >
        <span class="text-sm text-gray-07 font-medium mb-1.5">Nome do Documento:</span>
        <input
          :value="getDocumentNameForFile(file)"
          type="text"
          :class="[
            'w-full border rounded-xl p-4',
            isTitleDuplicateInBatch(file) ? 'border-red-16' : 'border-gray-02',
          ]"
          placeholder="Escreva aqui o nome do documento"
          @input="handleDocumentNameUpdate(file, ($event.target as HTMLInputElement).value)"
        >
      </div>
      <div
        v-if="multiple && !isExternal"
        class="flex flex-col mt-6 w-1/3"
      >
        <span class="text-sm text-gray-07 font-medium mb-1.5"> Responsável pela aprovação </span>
        <SelectInput
          :model-value="returnManager(file)"
          :options="managerSelectOptions"
          label="Selecione um gestor"
          @update:model-value="handleUpdateModelValue($event, file)"
        />
      </div>
      <div
        v-if="multiple && !isExternal"
        class="flex flex-col mt-3"
      >
        <span class="text-sm text-gray-07 font-medium mb-1.5">
          Changelog <span class="text-red-500">*</span>
        </span>
        <textarea
          id="changelog"
          :value="returnChangelog(file)"
          name="changelog"
          :class="[
            'w-full border rounded-xl p-4 resize-none',
            shouldShowChangelogError(file) || shouldShowChangelogLengthError(file)
              ? 'border-red-500'
              : 'border-gray-02',
          ]"
          placeholder="Escreva o que mudou no documento"
          required
          :maxlength="MAX_CHANGELOG_LENGTH"
          @input="handleChangelogUpdate(($event.target as HTMLTextAreaElement).value, file)"
          @blur="handleChangelogBlur(file)"
        />
        <span
          v-if="shouldShowChangelogError(file)"
          class="text-red-500 text-xs mt-1"
        >
          Changelog é obrigatório
        </span>
        <span
          v-else-if="shouldShowChangelogLengthError(file)"
          class="text-red-500 text-xs mt-1"
        >
          O changelog não pode exceder 2000 caracteres
        </span>
      </div>
    </div>
  </div>
</template>
